import type { Terminal as XTerm } from '@xterm/xterm';
import { useCallback, useEffect, useState } from 'react';
import { Terminal, type TerminalProps } from './Terminal';
import type { ShellDriver } from '~/lib/shell/driver';
import { webcontainer } from '~/lib/webcontainer';
import { useInitRef } from '~/utils/init-ref';
import { runShellInTerminal } from '~/utils/shell';

interface ShellTerminalProps extends TerminalProps {
  driver: ShellDriver;
}

export function BoltTerminal({ driver, ...terminalProps }: ShellTerminalProps) {
  const [terminal, setTerminal] = useState<{ instance: XTerm } | null>(null);

  const abortRef = useInitRef(() => new AbortController());

  const handleTerminalRef = useCallback((terminalInstance: { instance: XTerm } | null) => {
    setTerminal(terminalInstance);
  }, []);

  useEffect(() => {
    if (!terminal) {
      return undefined;
    }

    let unmounted = false;

    const initializeShell = async () => {
      const instance = await webcontainer;

      if (unmounted || !terminal) {
        return;
      }

      const terminalInstance = terminal.instance;

      const shell = runShellInTerminal(instance, terminalInstance, {
        signal: abortRef.current.signal,
        env: { npm_config_yes: true },
      });

      await shell.ready();

      if (!unmounted) {
        // we only start the driver if the component was not yet unmounted in the mean time
        driver.start(shell, terminalInstance);
      }
    };

    initializeShell();

    return (): void => {
      unmounted = true;
    };
  }, [terminal]);

  return <Terminal {...terminalProps} ref={handleTerminalRef} />;
}
